<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteAdmin } from '@/composables/useSiteAdmin'
import { formatPrice, itemRef } from '@/Utils/siteAccess'
import { contentKindLabel } from '@/Utils/siteContentKind'
import { formatMoney, isoDay, productTitle, humanizeRefs } from '@/Utils/siteTransactions'
import { saveBlob, slugForFile } from '@/Utils/downloadFile'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import FormPagination from '@/components/Atoms/FormPagination.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import TabGroup from '@/components/Atoms/TabGroup.vue'
import TabItem from '@/components/Atoms/TabItem.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue'

// Viewers = the owner's paywall audience (shared by all of the owner's sites;
// purchase counts are for this site). Backed by castr /sites/:id/viewers/*.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const route = useRoute()
const admin = useSiteAdmin(() => props.siteId)

const PER_PAGE = 25
const STATUS_OPTIONS = [
	{ value: '', text: 'Active & deactivated' },
	{ value: 'active', text: 'Active' },
	{ value: 'deactivated', text: 'Deactivated' },
]
const loading = ref(true)
const loadError = ref(null)
const rows = ref([])
const total = ref(0)
const page = ref(1)
// The Transactions panel links here with `?q=<email>`.
const q = ref(typeof route.query.q === 'string' ? route.query.q : '')
const statusFilter = ref('')
const planFilter = ref('')
// Viewers are site-specific (castr §6h: `scope=site` is the default). The
// secondary link widens the list to every site of the owner (`scope=owner`).
const allSites = ref(false)
// castr marks the enrichment (plan / spent / activity) as partial when its
// transactions walk was truncated or unavailable — paywall fields are unaffected.
const partial = ref(false)
const scope = ref('site')
const lockedOnly = computed(() => statusFilter.value === 'deactivated')
const filtering = computed(() => Boolean(q.value.trim() || statusFilter.value || planFilter.value))

// drawer
const drawerOpen = ref(false)
const drawerLoading = ref(false)
const viewer = ref(null)
const drawerTab = ref('access')
const editForm = ref({ name: '', email: '' })
const savingEdit = ref(false)
const busy = ref(false)
const grantForm = ref({ mode: 'product', product: '', refs: [], expires: '', reason: '' })
const granting = ref(false)

const paywallUnlinked = computed(() => !props.site.billing?.paywallSiteId)
// castr 503 reasons are never the owner's doing — say what is actually wrong.
const REASON_TEXT = {
	paywall_runtime_key_rejected: 'Castr could not authenticate with the Paywall service (its runtime key was rejected). This is a server configuration problem — viewers, grants and locks are unavailable until it is fixed. Please contact support.',
	paywall_site_unconfigured: 'This site is not linked to your Paywall account yet. Retry publish from the Overview tab.',
}
const describeError = (err, fallback) => REASON_TEXT[err?.reason] || err?.message || fallback
const loadErrorText = computed(() => describeError(loadError.value, 'Could not load viewers.'))

// Shared by the list and the CSV export (§6c/§6h: `locked=1` is the paywall-side
// filter, `status=active` / `plan=` / `scope=owner` are applied by castr; no
// `scope` = this site only).
const filterParams = () => _.omitBy({
	q: q.value.trim() || undefined,
	locked: lockedOnly.value ? 1 : undefined,
	status: statusFilter.value === 'active' ? 'active' : undefined,
	plan: planFilter.value || undefined,
	scope: allSites.value ? 'owner' : undefined,
}, _.isUndefined)

const load = async () => {
	loading.value = true
	try {
		const res = await SiteAdminService.listViewers(props.siteId, { ...filterParams(), page: page.value, per_page: PER_PAGE })
		rows.value = res?.data || []
		total.value = res?.meta?.total ?? rows.value.length
		partial.value = res?.partial === true || rows.value.some((r) => r.partial === true)
		scope.value = res?.scope || (allSites.value ? 'owner' : 'site')
		loadError.value = null
	} catch (err) {
		loadError.value = err
		rows.value = []
	}
	loading.value = false
}
const debouncedSearch = _.debounce(() => { page.value = 1; load() }, 350)
watch(q, debouncedSearch)
watch(page, load)
watch([statusFilter, planFilter, allSites], () => { page.value = 1; load() })
const clearFilters = () => {
	q.value = ''
	statusFilter.value = ''
	planFilter.value = ''
}

onMounted(async () => {
	await Promise.all([load(), admin.loadPlans(), admin.loadContent()])
})

const exporting = ref(false)
const exportCsv = async () => {
	if (exporting.value) return
	exporting.value = true
	try {
		const blob = await SiteAdminService.downloadViewersCsv(props.siteId, filterParams())
		saveBlob(blob instanceof Blob ? blob : new Blob([blob], { type: 'text/csv;charset=utf-8' }), `${slugForFile(props.site.slug || props.site.name)}-viewers-${isoDay()}.csv`)
	} catch (err) {
		notify({ group: 'error', text: describeError(err, 'Could not export the viewers.') })
	}
	exporting.value = false
}

// --- table cells -----------------------------------------------------------------
const planFilterOptions = computed(() => [
	{ value: '', text: 'Any plan' },
	...admin.plans.value.map((p) => ({ value: p.public_id, text: p.active === false ? `${p.title} (archived)` : p.title })),
])
const isDeactivated = (row) => row.status === 'deactivated' || row.locked === true
const statusUnknown = (row) => row.status == null && row.locked == null
/** `total_spent` is null with `currency: 'mixed'` when the viewer paid in several currencies. */
const spentLines = (row) => {
	if (row.currency === 'mixed' && row.total_spent_by_currency) return Object.entries(row.total_spent_by_currency).map(([cur, amount]) => formatMoney(amount, cur))
	if (row.total_spent === null || row.total_spent === undefined) return []
	return [formatMoney(row.total_spent, row.currency)]
}
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '')
const relativeDay = (v) => {
	if (!v) return ''
	const days = Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000)
	if (days <= 0) return 'Today'
	if (days === 1) return 'Yesterday'
	if (days < 30) return `${days} days ago`
	return new Date(v).toLocaleDateString()
}
/** `last_seen` (paywall sign-in, castr §6h) when present; else castr's `last_activity` (purchase / payment / refund on this site). */
const activity = (row) => {
	const seen = row.last_seen || row.last_seen_at
	if (seen) return { text: relativeDay(seen), sub: 'last seen', title: fmtDateTime(seen) }
	if (row.last_activity) return { text: relativeDay(row.last_activity), sub: row.last_activity_source === 'invoice' ? 'last purchase' : 'last activity', title: fmtDateTime(row.last_activity) }
	return null
}

const openViewer = async (row) => {
	drawerOpen.value = true
	drawerLoading.value = true
	drawerTab.value = 'access'
	viewer.value = null
	try {
		viewer.value = await SiteAdminService.getViewer(props.siteId, row.id)
		editForm.value = { name: viewer.value.user?.name || '', email: viewer.value.user?.email || '' }
		grantForm.value = { mode: 'product', product: admin.activePlans.value[0]?.public_id || '', refs: [], expires: '', reason: '' }
	} catch (err) {
		notify({ group: 'error', text: describeError(err, 'Could not load the viewer.') })
		drawerOpen.value = false
	}
	drawerLoading.value = false
}

const refreshViewer = async () => {
	if (!viewer.value) return
	viewer.value = await SiteAdminService.getViewer(props.siteId, viewer.value.user.id)
	await load()
}

const editDirty = computed(() => viewer.value && (editForm.value.name.trim() !== (viewer.value.user?.name || '') || editForm.value.email.trim() !== (viewer.value.user?.email || '')))
const saveEdit = async () => {
	if (!editDirty.value) return
	savingEdit.value = true
	try {
		const patch = {}
		if (editForm.value.name.trim() !== (viewer.value.user?.name || '')) patch.name = editForm.value.name.trim()
		if (editForm.value.email.trim() !== (viewer.value.user?.email || '')) patch.email = editForm.value.email.trim()
		await SiteAdminService.updateViewer(props.siteId, viewer.value.user.id, patch)
		notify({ group: 'success', text: 'Viewer updated.' })
		await refreshViewer()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not update the viewer.' })
	}
	savingEdit.value = false
}

const sendReset = async () => {
	busy.value = true
	try {
		await SiteAdminService.sendViewerPasswordReset(props.siteId, viewer.value.user.id)
		notify({ group: 'success', text: `Password reset email sent to ${viewer.value.user.email}.` })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not send the reset email.' })
	}
	busy.value = false
}

// --- lock / unlock ("deactivate") -----------------------------------------------
// A lock is owner-wide (paywall viewers are shared by all the owner's sites):
// the viewer cannot sign in anywhere and existing sessions end; purchases and
// history are kept. `cancel_subscriptions` additionally cancels their active
// recurring purchases on this site (access runs to the end of the paid period).
// Subscriptions are not offered in this version of Sites: the lock flow only
// mentions them when the site still has a subscription plan sold earlier.
const hasSubscriptions = computed(() => admin.planCapabilities.value?.recurring === true || admin.plans.value.some((p) => p.recurring))
const lockOpen = ref(false)
const lockTarget = ref(null) // { id, name, email, locked, purchases? }
const lockForm = ref({ cancelSubscriptions: false, reason: '' })
const locking = ref(false)
const lockError = ref(null)
const viewerLabel = (v) => v?.name || v?.email || (v?.id ? `Viewer #${v.id}` : 'this viewer')
const requestLock = (target) => {
	lockTarget.value = target
	lockForm.value = { cancelSubscriptions: false, reason: '' }
	lockError.value = null
	lockOpen.value = true
}
const requestLockFromDrawer = () => requestLock({ ...viewer.value.user, locked: viewer.value.locked, activeSubscriptions: (viewer.value.invoices || []).filter((i) => i.recurring && i.status === 'active' && !i.cancelled_at).length })
const confirmLock = async () => {
	if (!lockTarget.value || locking.value) return
	locking.value = true
	lockError.value = null
	const target = lockTarget.value
	try {
		if (target.locked) {
			await SiteAdminService.unlockViewer(props.siteId, target.id)
			notify({ group: 'success', text: `${viewerLabel(target)} can sign in again.` })
		} else {
			const body = { cancel_subscriptions: lockForm.value.cancelSubscriptions === true }
			if (lockForm.value.reason.trim()) body.reason = lockForm.value.reason.trim().slice(0, 255)
			const res = await SiteAdminService.lockViewer(props.siteId, target.id, body)
			const cancelled = Array.isArray(res?.cancelled_subscriptions) ? res.cancelled_subscriptions.length : 0
			notify({ group: 'success', text: `${viewerLabel(target)} deactivated — they are signed out and cannot sign in.${cancelled ? ` ${cancelled} ${cancelled === 1 ? 'subscription' : 'subscriptions'} cancelled.` : ''}` })
		}
		lockOpen.value = false
		// Keep the list and the open drawer in step without a full re-fetch of the drawer when it is closed.
		rows.value = rows.value.map((r) => (r.id === target.id ? { ...r, locked: !target.locked, status: target.locked ? 'active' : 'deactivated', lock: target.locked ? null : { source: 'site', reason: lockForm.value.reason.trim() || null } } : r))
		if (viewer.value?.user?.id === target.id) await refreshViewer()
		else await load()
	} catch (err) {
		lockError.value = describeError(err, target.locked ? 'Could not reactivate this viewer.' : 'Could not deactivate this viewer.')
	}
	locking.value = false
}
const lockSourceLabel = (lock) => (lock?.source === 'viewer' ? 'deactivated by the viewer' : 'deactivated by you')

const toggleGrantRef = (ref, on) => {
	const set = new Set(grantForm.value.refs)
	if (on) set.add(ref)
	else set.delete(ref)
	grantForm.value.refs = [...set]
}
const canGrant = computed(() => (grantForm.value.mode === 'product' ? Boolean(grantForm.value.product) : grantForm.value.refs.length > 0) && !granting.value)
const grant = async () => {
	if (!canGrant.value) return
	granting.value = true
	try {
		const payload = { reason: grantForm.value.reason.trim() || undefined }
		if (grantForm.value.mode === 'product') payload.product = grantForm.value.product
		else payload.items = grantForm.value.refs
		if (grantForm.value.expires) payload.expires_at = new Date(`${grantForm.value.expires}T23:59:59`).toISOString()
		await SiteAdminService.grantViewerAccess(props.siteId, viewer.value.user.id, payload)
		notify({ group: 'success', text: 'Access granted.' })
		grantForm.value = { ...grantForm.value, refs: [], expires: '', reason: '' }
		await refreshViewer()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not grant access.' })
	}
	granting.value = false
}
const revoke = async (grantRow) => {
	busy.value = true
	try {
		await SiteAdminService.revokeViewerGrant(props.siteId, viewer.value.user.id, grantRow.public_id || grantRow.id)
		notify({ group: 'success', text: 'Access revoked.' })
		await refreshViewer()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not revoke access.' })
	}
	busy.value = false
}

// Grants and entitlements come back with the paywall's product title, which
// embeds raw refs ("Manual access (item:36)"); resolve them to content titles.
const lists = () => ({ content: admin.content.value, categories: admin.categories.value })
const grantLabel = (g) => productTitle(g, lists(), 'Manual access')
const titleOf = (text) => humanizeRefs(text, lists())
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString() : '—')
const planOptions = computed(() => admin.activePlans.value.map((p) => ({ value: p.public_id, text: `${p.title} · ${formatPrice(p)}` })))
const invoiceVariant = (s) => (s === 'active' ? 'success' : s === 'expired' ? 'muted' : 'warning')
</script>

<template>
	<div>
		<div class="mb-4 flex flex-wrap items-center gap-3">
			<div class="min-w-0 flex-1 basis-72">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Viewers</h2>
				<p class="mb-0 text-tiny text-surface-7" data-testid="viewers-scope" :data-scope="scope">
					<template v-if="scope === 'owner'">
						Viewers from all your sites — anyone registered on one of them can sign in here. Plan, spend and activity are for this site.
						<button type="button" class="text-main-light underline-offset-2 hover:underline" data-testid="viewers-this-site" @click="allSites = false">Show this site only</button>
					</template>
					<template v-else>
						People who registered, bought or were granted something on this site.
						<button type="button" class="text-main-light underline-offset-2 hover:underline" data-testid="viewers-all-sites" @click="allSites = true">Show viewers from all my sites</button>
					</template>
				</p>
			</div>
			<FormButton v-if="!paywallUnlinked" type="secondary" size="md" :disabled="exporting || loading || !!loadError" :is-loading="exporting" title="Download the viewers matching the current filters as CSV" data-testid="viewers-export" @click="exportCsv">Export CSV</FormButton>
		</div>

		<Alert v-if="paywallUnlinked" type="error" class="mb-4">This site is not linked to your Paywall account yet, so there are no viewer accounts. Retry publish from the Overview tab.</Alert>
		<Alert v-else-if="loadError" type="error" class="mb-4" data-testid="viewers-load-error">
			{{ loadErrorText }}
			<span v-if="loadError.reason" class="ml-1 font-mono text-tiny text-surface-8">({{ loadError.reason }})</span>
		</Alert>

		<div v-if="!paywallUnlinked" class="mb-3 flex flex-wrap items-center gap-2" data-testid="viewers-toolbar">
			<div class="relative flex min-w-[220px] flex-1 items-center sm:max-w-xs">
				<icon-base class="pointer-events-none absolute left-2.5 z-10 text-surface-7" :width="16" :height="16"><icon-search /></icon-base>
				<label for="viewers-search" class="sr-only">Search viewers</label>
				<FormInput id="viewers-search" v-model="q" placeholder="Search by email or name…" class="w-full" custom-classes="!pl-10" />
			</div>
			<div>
				<label for="viewers-status" class="sr-only">Filter by status</label>
				<FormSelect id="viewers-status" v-model="statusFilter" :options="STATUS_OPTIONS" class="!w-44" />
			</div>
			<div>
				<label for="viewers-plan" class="sr-only">Filter by plan</label>
				<FormSelect id="viewers-plan" v-model="planFilter" :options="planFilterOptions" class="!w-44" />
			</div>
			<span class="ml-auto text-tiny text-surface-7" data-testid="viewers-count">{{ total }} {{ lockedOnly ? 'deactivated' : '' }} {{ total === 1 ? 'viewer' : 'viewers' }}</span>
			<button v-if="filtering" type="button" class="text-tiny text-main-light underline-offset-2 hover:underline" data-testid="viewers-clear-filters" @click="clearFilters">Clear filters</button>
		</div>

		<div v-if="lockedOnly && !paywallUnlinked" class="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-warning/10 px-3 py-2 text-tiny text-warning ring-1 ring-warning/30" data-testid="viewers-locked-filter">
			<span class="font-medium">Showing deactivated viewers only.</span>
			<span class="text-surface-8">These viewers cannot sign in on any of your sites until you reactivate them.</span>
			<button type="button" class="ml-auto underline hover:text-white" @click="statusFilter = ''">Show all viewers</button>
		</div>

		<p v-if="partial && !loading && rows.length" class="mb-3 flex items-start gap-1.5 text-tiny text-surface-7" data-testid="viewers-partial">
			<icon-base class="mt-0.5 shrink-0 text-warning" :width="14" :height="14"><icon-info-small /></icon-base>
			<span>Plan, total spent and last activity may be incomplete for some viewers — the purchase history could not be read in full right now. Account status and purchase counts are unaffected.</span>
		</p>

		<Spinner v-if="loading" text="Loading viewers…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<EmptyState v-else-if="!rows.length && !paywallUnlinked && !loadError" :title="lockedOnly ? 'No deactivated viewers' : (filtering ? 'No matching viewers' : 'No viewers yet')"
			:subtitle="lockedOnly ? (q ? 'No deactivated viewer matches your search.' : 'Every viewer can sign in. Deactivate one from its row or drawer.') : (filtering ? 'Try a different search or clear the filters.' : (scope === 'owner' ? 'Nobody has registered on any of your sites yet.' : 'Viewers appear here as soon as someone registers or buys on this site.'))">
			<template v-if="filtering" #actions>
				<FormButton type="secondary" size="sm" @click="clearFilters">Clear filters</FormButton>
			</template>
		</EmptyState>

		<div v-else-if="rows.length" class="overflow-x-auto rounded-lg bg-surface-2 p-2">
			<table class="w-full min-w-[640px] border-separate border-spacing-y-1 text-left" data-testid="viewers-table">
				<thead>
					<tr class="text-[11px] uppercase tracking-wider text-surface-6">
						<th class="whitespace-nowrap px-2 pb-1 font-semibold">Viewer</th>
						<th class="whitespace-nowrap px-2 pb-1 font-semibold">Plan</th>
						<th class="whitespace-nowrap px-2 pb-1 font-semibold"><span title="Total paid on this site, net of refunds">Spent</span></th>
						<th class="whitespace-nowrap px-2 pb-1 font-semibold">
							<span class="inline-flex items-center gap-1">
								Activity
								<Tooltip content="Last sign-in when known; otherwise the newest purchase, payment or refund on this site." popper-class="max-w-[240px] text-sm normal-case tracking-normal break-words">
									<icon-base class="cursor-help text-surface-6 hover:text-white" :width="12" :height="12" aria-label="About last activity"><icon-info-small /></icon-base>
								</Tooltip>
							</span>
						</th>
						<th class="whitespace-nowrap px-2 pb-1 font-semibold">Status</th>
						<th class="whitespace-nowrap px-2 pb-1 text-right font-semibold"><span class="sr-only">Actions</span></th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in rows" :key="row.id" class="group cursor-pointer" :class="{ 'opacity-80': isDeactivated(row) }" :data-testid="`viewer-${row.id}`" :data-status="isDeactivated(row) ? 'deactivated' : 'active'" @click="openViewer(row)">
						<td class="rounded-l-lg bg-surface-3/60 px-2 py-2 align-middle transition-colors group-hover:bg-surface-3">
							<button type="button" class="flex min-w-0 max-w-[220px] items-center gap-2.5 text-left" @click.stop="openViewer(row)">
								<span class="grid size-7 shrink-0 place-items-center rounded-full bg-surface-4 text-tiny font-semibold uppercase text-surface-8" :class="{ 'opacity-50': isDeactivated(row) }">{{ (row.name || row.email || '?').slice(0, 1) }}</span>
								<span class="min-w-0">
									<span class="block truncate text-sm font-medium" :class="isDeactivated(row) ? 'text-surface-8' : 'text-white'">{{ row.name || row.email }}</span>
									<span class="block truncate text-tiny text-surface-7" :title="row.member_since ? `Registered ${fmtDate(row.member_since)}` : undefined">
										<template v-if="row.name">{{ row.email }}</template>
										<template v-else-if="row.member_since">since {{ fmtDate(row.member_since) }}</template>
									</span>
								</span>
							</button>
						</td>
						<td class="bg-surface-3/60 px-2 py-2 align-middle transition-colors group-hover:bg-surface-3" data-testid="viewer-plan">
							<span v-if="row.plan" class="block max-w-[200px] truncate text-sm text-surface-text-1" :title="row.plan">{{ row.plan }}</span>
							<span v-else class="text-sm text-surface-6" :title="row.partial ? 'Not available right now' : 'No active plan on this site'">—</span>
							<span v-if="row.purchases" class="block text-tiny text-surface-7">{{ row.purchases.active ? `${row.purchases.active} active · ` : '' }}{{ row.purchases.total }} {{ row.purchases.total === 1 ? 'purchase' : 'purchases' }}</span>
						</td>
						<td class="bg-surface-3/60 px-2 py-2 align-middle transition-colors group-hover:bg-surface-3" data-testid="viewer-spent">
							<template v-if="spentLines(row).length">
								<span v-for="line in spentLines(row)" :key="line" class="block text-nowrap text-sm font-medium text-surface-text-1">{{ line }}</span>
							</template>
							<span v-else class="text-sm text-surface-6" :title="row.partial ? 'Not available right now' : 'Nothing paid on this site'">—</span>
						</td>
						<td class="bg-surface-3/60 px-2 py-2 align-middle transition-colors group-hover:bg-surface-3" data-testid="viewer-activity">
							<template v-if="activity(row)">
								<span class="block text-nowrap text-sm text-surface-text-1" :title="activity(row).title">{{ activity(row).text }}</span>
								<span class="block whitespace-nowrap text-tiny text-surface-7">{{ activity(row).sub }}</span>
							</template>
							<span v-else class="text-sm text-surface-6" :title="row.partial ? 'Not available right now' : 'No purchase activity on this site'">—</span>
						</td>
						<td class="bg-surface-3/60 px-2 py-2 align-middle transition-colors group-hover:bg-surface-3" data-testid="viewer-status">
							<template v-if="isDeactivated(row)">
								<StatusPill pill size="sm" variant="warning" :title="lockSourceLabel(row.lock)">Deactivated</StatusPill>
								<span v-if="row.lock?.reason" class="mt-0.5 block max-w-[160px] truncate text-tiny text-surface-7" :title="row.lock.reason">{{ row.lock.reason }}</span>
							</template>
							<span v-else-if="statusUnknown(row)" class="text-tiny text-surface-6" title="Account status could not be read right now">Unknown</span>
							<StatusPill v-else pill size="sm" variant="success">Active</StatusPill>
						</td>
						<td class="rounded-r-lg bg-surface-3/60 px-2 py-2 text-right align-middle transition-colors group-hover:bg-surface-3">
							<FormButton v-if="!statusUnknown(row)" type="link" size="sm" class="shrink-0 !px-1 underline-offset-2 hover:underline" :class="isDeactivated(row) ? '' : 'hover:!text-error-light'" :aria-label="`${isDeactivated(row) ? 'Reactivate' : 'Deactivate'} ${row.name || row.email}`" @click.stop="requestLock({ ...row, locked: isDeactivated(row) })">
								{{ isDeactivated(row) ? 'Reactivate' : 'Deactivate' }}
							</FormButton>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div v-if="total > PER_PAGE" class="mt-4 flex justify-center">
			<FormPagination v-model="page" :total-rows="total" :per-page="PER_PAGE" />
		</div>

		<!-- Viewer drawer -->
		<ModalBox v-model="drawerOpen" :title="viewer?.user?.name || viewer?.user?.email || 'Viewer'" placement="right" size="md" scroll-body>
			<div class="px-4 pb-4 sm:px-6 sm:pb-6">
				<Spinner v-if="drawerLoading" text="Loading…" classes="text-surface-8 my-12" spinner-color="var(--c-dark-8)" spinner-size="15px" />
				<template v-else-if="viewer">
					<div class="mb-4 flex flex-wrap items-center gap-2" data-testid="viewer-drawer-status">
						<StatusPill v-if="viewer.locked" pill size="sm" variant="warning">Deactivated</StatusPill>
						<StatusPill v-else pill size="sm" variant="success">Active</StatusPill>
						<span class="min-w-0 flex-1 truncate text-tiny text-surface-7">
							{{ viewer.user.email }} · registered {{ fmtDate(viewer.user.member_since) }}
							<template v-if="viewer.locked"> · {{ lockSourceLabel(viewer.lock) }}{{ viewer.lock?.reason ? ` (${viewer.lock.reason})` : '' }}</template>
						</span>
						<FormButton :type="viewer.locked ? 'secondary' : 'danger'" size="sm" class="shrink-0" :disabled="busy" @click="requestLockFromDrawer">
							{{ viewer.locked ? 'Reactivate' : 'Deactivate' }}
						</FormButton>
					</div>

					<TabGroup type="segmented" class="mb-4">
						<TabItem :is-active="drawerTab === 'access'" @click="drawerTab = 'access'">Access</TabItem>
						<TabItem :is-active="drawerTab === 'history'" @click="drawerTab = 'history'">Purchases</TabItem>
						<TabItem :is-active="drawerTab === 'account'" @click="drawerTab = 'account'">Account</TabItem>
					</TabGroup>

					<!-- Access: entitlements + grants -->
					<div v-if="drawerTab === 'access'" class="flex flex-col gap-4">
						<section>
							<h3 class="mb-2 text-sm font-semibold text-white">Current access</h3>
							<p v-if="!viewer.entitlements?.length" class="mb-0 text-tiny text-surface-7">No active plans or grants on this site.</p>
							<ul v-else class="m-0 flex list-none flex-col gap-1.5 p-0">
								<li v-for="ent in viewer.entitlements" :key="ent.product?.public_id" class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-2 text-sm">
									<span class="min-w-0 flex-1 truncate text-white" data-testid="viewer-entitlement-title">{{ titleOf(ent.product?.title) }}</span>
									<StatusPill pill size="sm" :variant="invoiceVariant(ent.invoice?.status)">{{ ent.invoice?.status }}</StatusPill>
									<span class="shrink-0 text-tiny text-surface-7">{{ ent.grant ? 'granted' : formatPrice(ent.invoice || ent.product) }} · until {{ fmtDate(ent.invoice?.expires_at) }}</span>
								</li>
							</ul>
						</section>

						<section>
							<h3 class="mb-2 text-sm font-semibold text-white">Manual grants</h3>
							<ul v-if="viewer.grants?.length" class="m-0 mb-3 flex list-none flex-col gap-1.5 p-0">
								<li v-for="g in viewer.grants" :key="g.public_id || g.id" class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-2 text-sm">
									<span class="min-w-0 flex-1 truncate text-white" data-testid="viewer-grant-title">{{ grantLabel(g) }}</span>
									<span class="shrink-0 text-tiny text-surface-7">{{ g.active === false || g.revoked_at ? 'revoked' : `until ${fmtDate(g.expires_at)}` }}</span>
									<FormButton v-if="!(g.active === false || g.revoked_at)" type="danger" size="sm" :disabled="busy" @click="revoke(g)">Revoke</FormButton>
								</li>
							</ul>
							<div class="rounded-lg bg-surface-3 p-3">
								<p class="mb-2 text-tiny font-medium text-surface-8">Grant free access</p>
								<TabGroup type="segmented" class="mb-3">
									<TabItem :is-active="grantForm.mode === 'product'" @click="grantForm.mode = 'product'">A plan</TabItem>
									<TabItem :is-active="grantForm.mode === 'items'" @click="grantForm.mode = 'items'">Specific content</TabItem>
								</TabGroup>
								<FormSelect v-if="grantForm.mode === 'product'" id="grant-product" v-model="grantForm.product" label="Plan" :options="planOptions.length ? planOptions : [{ value: '', text: 'No plans yet' }]" />
								<div v-else class="max-h-48 overflow-y-auto rounded bg-surface-2 p-2" data-testid="grant-items">
									<p v-if="!admin.content.value.length" class="mb-0 text-tiny text-surface-7">Add content first.</p>
									<label v-for="item in admin.content.value" :key="`gi-${item.id}`" class="flex items-center gap-2 py-1 text-sm text-surface-text-1">
										<FormCheckbox :name="`grant-item-${item.id}`" :model-value="grantForm.refs.includes(itemRef(item.id))" @update:model-value="toggleGrantRef(itemRef(item.id), $event)" />
										<span class="truncate">{{ item.title }}</span><span class="ml-auto text-tiny text-surface-7">{{ contentKindLabel(item) }}</span>
									</label>
								</div>
								<div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
									<FormInput id="grant-expires" v-model="grantForm.expires" type="date" label="Expires (optional)" />
									<FormInput id="grant-reason" v-model="grantForm.reason" label="Reason (optional)" placeholder="e.g. Press pass" input-classes="placeholder:text-surface-6" />
								</div>
								<FormButton size="md" class="mt-3 w-full justify-center" :disabled="!canGrant" :is-loading="granting" @click="grant">Grant access</FormButton>
							</div>
						</section>
					</div>

					<!-- Purchase history -->
					<div v-else-if="drawerTab === 'history'">
						<p v-if="!viewer.invoices?.length" class="mb-0 text-tiny text-surface-7">No purchases on this site yet.</p>
						<ul v-else class="m-0 flex list-none flex-col gap-1.5 p-0">
							<li v-for="inv in viewer.invoices" :key="inv.public_id" class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-2 text-sm">
								<span class="min-w-0 flex-1">
									<span class="block truncate text-white">{{ titleOf(inv.product_title || inv.product?.title) }}</span>
									<span class="block text-tiny text-surface-7">{{ fmtDate(inv.created_at) }} · {{ inv.free ? 'free' : formatPrice(inv) }}<template v-if="inv.recurring"> · recurring</template><template v-if="inv.cancelled_at"> · cancelled</template></span>
								</span>
								<StatusPill pill size="sm" :variant="invoiceVariant(inv.status)">{{ inv.status }}</StatusPill>
							</li>
						</ul>
					</div>

					<!-- Account -->
					<div v-else class="flex flex-col gap-4">
						<section class="flex flex-col gap-3">
							<FormInput id="viewer-name" v-model="editForm.name" label="Name" :maxlength="191" />
							<FormInput id="viewer-email" v-model="editForm.email" label="Email" type="email" />
							<FormButton size="md" class="self-start" :disabled="!editDirty || savingEdit" :is-loading="savingEdit" @click="saveEdit">Save</FormButton>
						</section>
						<section class="rounded-lg bg-surface-3 p-3">
							<p class="mb-2 text-sm font-medium text-white">Password</p>
							<p class="mb-2 text-tiny text-surface-7">Sends the viewer a reset link that lands on {{ site.primaryHost }}.</p>
							<FormButton type="secondary" size="sm" :disabled="busy" @click="sendReset">Send password reset</FormButton>
						</section>
						<section class="rounded-lg bg-surface-3 p-3 ring-1" :class="viewer.locked ? 'ring-success/40' : 'ring-error/30'">
							<p class="mb-2 text-sm font-medium text-white">{{ viewer.locked ? 'Reactivate account' : 'Deactivate account' }}</p>
							<p class="mb-2 text-tiny text-surface-7">
								<template v-if="viewer.locked">Lets the viewer sign in again on all your sites.<template v-if="hasSubscriptions"> Subscriptions cancelled at deactivation are not restored.</template></template>
								<template v-else>Signs the viewer out everywhere and blocks sign-in on all your sites. Purchases and history are kept<template v-if="hasSubscriptions">; you can also cancel their subscriptions</template>.</template>
							</p>
							<FormButton :type="viewer.locked ? 'secondary' : 'danger'" size="sm" :disabled="busy" @click="requestLockFromDrawer">{{ viewer.locked ? 'Reactivate' : 'Deactivate' }}</FormButton>
						</section>
					</div>
				</template>
			</div>
		</ModalBox>

		<!-- Deactivate / reactivate confirmation -->
		<ModalBox v-model="lockOpen" :title="lockTarget?.locked ? 'Reactivate this viewer?' : 'Deactivate this viewer?'" size="sm" z-index="z-[60]">
			<div v-if="lockTarget" class="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="lock-modal">
				<Alert v-if="lockError" type="error" class="!mb-0">{{ lockError }}</Alert>
				<p class="mb-0 text-sm text-surface-text-1"><span class="font-medium text-white">{{ viewerLabel(lockTarget) }}</span><template v-if="lockTarget.name && lockTarget.email"> · {{ lockTarget.email }}</template></p>
				<template v-if="lockTarget.locked">
					<p class="mb-0 text-tiny text-surface-7">They can sign in again on all your sites right away. Purchases they still hold work as before<template v-if="hasSubscriptions">; subscriptions cancelled when they were deactivated are not restored</template>.</p>
				</template>
				<template v-else>
					<ul class="m-0 list-disc pl-5 text-tiny text-surface-7">
						<li>They cannot sign in on any of your sites and are signed out everywhere now.</li>
						<li>Purchases, history and their account are kept — nothing is deleted.</li>
						<li v-if="hasSubscriptions">Active subscriptions keep renewing unless you cancel them below.</li>
					</ul>
					<div v-if="hasSubscriptions" class="rounded-lg bg-surface-3 px-3 py-2.5">
						<FormCheckbox name="lock-cancel-subscriptions" :input-value="true" size="md" label="Also cancel their active subscriptions on this site"
							:model-value="lockForm.cancelSubscriptions" @update:model-value="lockForm.cancelSubscriptions = $event" />
						<p class="mb-0 mt-1 pl-5 text-tiny text-surface-7">
							Recurring plans stop renewing; access runs to the end of the period already paid. One-time purchases are not affected.
							<template v-if="lockTarget.activeSubscriptions"> {{ lockTarget.activeSubscriptions }} active {{ lockTarget.activeSubscriptions === 1 ? 'subscription' : 'subscriptions' }} on this site.</template>
						</p>
					</div>
					<FormInput id="lock-reason" v-model="lockForm.reason" label="Reason (optional, shown to you only)" placeholder="e.g. Chargeback / abuse" input-classes="placeholder:text-surface-6" :maxlength="255" />
				</template>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton :type="lockTarget?.locked ? 'main' : 'danger'" class="w-full justify-center sm:w-auto" :disabled="locking" :is-loading="locking" @click="confirmLock">
						{{ lockTarget?.locked ? 'Reactivate' : (lockForm.cancelSubscriptions ? 'Deactivate and cancel subscriptions' : 'Deactivate') }}
					</FormButton>
					<FormButton type="secondary" class="w-full justify-center sm:w-auto" @click="lockOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>
	</div>
</template>
